import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { CycleReservesPanel } from "@/components/cycles/CycleReservesPanel";
import { Button } from "@/components/ui/Button";
import { Pagination } from "@/components/ui/Pagination";
import { useGasStatus } from "@/hooks/useGasStatus";
import {
	GAS_DONATE_PRESETS,
	formatNsDate,
	useDonateGasIcp,
	useGasSupporters,
	useSetGasDonationMessage,
} from "@/hooks/useGasStation";
import { useGasFundingStatus } from "@/hooks/useGovernance";
import { useLiquidityTreasury } from "@/hooks/useTreasury";
import { useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import { ROUTES } from "@/lib/routes";
import { cn, formatNum, shortPrincipal } from "@/lib/utils";
import { useState } from "react";
import { Link } from "react-router-dom";

const WALL_PAGE = 8;

/** Cycle Gas Station — donate Ops ICP + Supporters Wall. */
export default function CyclesPage() {
	const { data, isLoading } = useGasStatus();
	const { data: liq } = useLiquidityTreasury();
	const { data: gas } = useGasFundingStatus();
	const { balances, isAuthenticated } = useAuth();
	const donate = useDonateGasIcp();
	const setMessage = useSetGasDonationMessage();
	const [preset, setPreset] = useState<(typeof GAS_DONATE_PRESETS)[number]>(1);
	const [wallOffset, setWallOffset] = useState(0);
	const { data: wall, isLoading: wallLoading } = useGasSupporters(
		wallOffset,
		WALL_PAGE,
	);
	const [pendingDonationId, setPendingDonationId] = useState<number | null>(
		null,
	);
	const [wallMsg, setWallMsg] = useState("");

	const tone =
		data?.label === "Critical"
			? "text-danger"
			: data?.label === "Low"
				? "text-warning"
				: "text-primary";

	const opsIcp = liq?.opsIcp ?? gas?.opsIcp ?? 0;
	const maxSpendable = depositableTokenMax(balances.ICP);
	const canAfford = maxSpendable + 1e-12 >= preset;

	async function onDonate() {
		const result = await donate.mutateAsync(preset);
		setPendingDonationId(result.id);
		setWallMsg("");
	}

	async function onPostMessage() {
		if (pendingDonationId == null) return;
		await setMessage.mutateAsync({
			id: pendingDonationId,
			message: wallMsg,
		});
		setPendingDonationId(null);
		setWallMsg("");
	}

	const events = wall?.events ?? [];
	const total = wall?.total ?? 0;

	return (
		<div className="container mx-auto max-w-5xl space-y-6 px-4 py-6 sm:py-8 pb-16">
			<div>
				<p className="section-eyebrow">Gas</p>
				<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
					Cycle Gas Station
				</h1>
				<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
					Donate ICP to the protocol Ops sleeve that funds canister cycles.
					Contribute first, then leave a short message on the Supporters Wall.
					Daily balance and spend telemetry for monitored canisters is below.
				</p>
			</div>

			<CycleReservesPanel />

			<div className="grid gap-6 lg:grid-cols-2 lg:items-start">
				{/* Left — Contribute */}
				<div className="space-y-4">
					<div className="card-surface space-y-4 p-5">
						{isLoading || !data ? (
							<div className="skeleton-shimmer h-20" />
						) : (
							<>
								<div className="flex items-center justify-between">
									<span className="text-xs font-semibold uppercase text-muted-foreground">
										Status
									</span>
									<span className={cn("text-sm font-bold", tone)}>
										{data.label}
									</span>
								</div>
								{data.note ? (
									<p className="text-[11px] leading-snug text-muted-foreground">
										{data.note}
									</p>
								) : null}
								{data.label === "Unknown" ? (
									<p className="text-[11px] leading-snug text-muted-foreground">
										Unknown = cycles health query failed. Ops ICP sleeve below is
										still live from protocol gas funding when available (
										{formatNum(opsIcp, 2)} ICP).
									</p>
								) : null}
								<div
									className={cn(
										"grid gap-3",
										data.opsAvailable ? "grid-cols-2" : "grid-cols-1",
									)}
								>
									{data.opsAvailable ? (
										<div className="rounded-xl bg-muted/50 p-3">
											<p className="text-[10px] uppercase text-muted-foreground">
												Ops cycles
											</p>
											<p className="font-mono-amount text-lg font-bold">
												{formatNum(data.opsCyclesT, 1)}T
											</p>
										</div>
									) : null}
									<div className="rounded-xl bg-muted/50 p-3">
										<p className="text-[10px] uppercase text-muted-foreground">
											Protocol cycles
										</p>
										<p className="font-mono-amount text-lg font-bold">
											{formatNum(data.protocolCyclesT, 1)}T
										</p>
									</div>
								</div>
							</>
						)}
					</div>

					<div className="card-surface space-y-4 p-5">
						<div className="flex items-center gap-2">
							<TokenIcon token="ICP" size="md" />
							<div>
								<p className="text-sm font-semibold">Contribute Ops ICP</p>
								<p className="text-xs text-muted-foreground">
									Presets only · credits protocol treasury Ops sleeve
								</p>
							</div>
						</div>

						<p className="font-mono-amount text-2xl font-bold">
							{formatNum(opsIcp, 2)}{" "}
							<span className="text-base font-semibold text-muted-foreground">
								ICP in ops
							</span>
						</p>
						<p className="text-[11px] text-muted-foreground">
							Target {formatNum(gas?.gasTargetIcp ?? 1000, 0)} ICP · Floor{" "}
							{formatNum(gas?.gasFloorIcp ?? 250, 0)} ICP
						</p>

						<div className="flex flex-wrap gap-2">
							{GAS_DONATE_PRESETS.map((n) => (
								<button
									key={n}
									type="button"
									onClick={() => setPreset(n)}
									className={cn(
										"min-h-11 flex-1 rounded-xl border px-3 text-sm font-bold sm:flex-none",
										preset === n
											? "border-primary bg-primary/15 text-primary"
											: "border-border text-muted-foreground",
									)}
								>
									{n} ICP
								</button>
							))}
						</div>

						{isAuthenticated ? (
							<p className="text-xs text-muted-foreground">
								Wallet{" "}
								<span className="balance-chip">
									{formatNum(balances.ICP, 4)} ICP
								</span>
								{" · "}
								spendable {formatNum(maxSpendable, 4)}
							</p>
						) : (
							<p className="text-xs text-muted-foreground">
								Connect, hold ICP in your wallet, then donate.
							</p>
						)}

						<RequireAuthAction
							labelWhenUnauthed="Connect to donate"
							disabled={donate.isPending || !canAfford}
							onAuthenticated={() => void onDonate()}
						>
							{donate.isPending
								? "Donating…"
								: canAfford
									? `Donate ${preset} ICP`
									: "Insufficient ICP"}
						</RequireAuthAction>

						{pendingDonationId != null ? (
							<div className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-4">
								<p className="text-sm font-semibold">Add your wall message</p>
								<p className="text-xs text-muted-foreground">
									Optional · max 200 characters · one message per donation
								</p>
								<textarea
									value={wallMsg}
									onChange={(e) => setWallMsg(e.target.value.slice(0, 200))}
									rows={3}
									placeholder="Thanks for keeping MotokoMaker online…"
									className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
								/>
								<div className="flex items-center justify-between gap-2">
									<span className="text-[11px] text-muted-foreground">
										{wallMsg.trim().length}/200
									</span>
									<div className="flex gap-2">
										<button
											type="button"
											className="text-xs font-semibold text-muted-foreground"
											onClick={() => {
												setPendingDonationId(null);
												setWallMsg("");
											}}
										>
											Skip
										</button>
										<Button
											size="sm"
											disabled={
												setMessage.isPending || wallMsg.trim().length === 0
											}
											onClick={() => void onPostMessage()}
										>
											{setMessage.isPending ? "Posting…" : "Post message"}
										</Button>
									</div>
								</div>
							</div>
						) : null}

						<p className="text-[11px] text-muted-foreground">
							Flow: login → hold ICP → donate → optional message. Also see{" "}
							<Link to={ROUTES.treasury} className="font-semibold text-primary">
								Treasury
							</Link>
							.
						</p>
					</div>
				</div>

				{/* Right — Supporters Wall */}
				<div className="card-surface space-y-4 p-5 lg:min-h-[28rem]">
					<div>
						<p className="section-eyebrow">Community</p>
						<h2 className="text-lg font-bold">Supporters Wall</h2>
						<p className="mt-1 text-xs text-muted-foreground">
							On-chain contributions to Ops ICP. Newest first.
						</p>
					</div>

					{wallLoading ? (
						<div className="space-y-2">
							{Array.from({ length: 4 }).map((_, i) => (
								<div key={i} className="skeleton-shimmer h-20 rounded-xl" />
							))}
						</div>
					) : events.length === 0 ? (
						<div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
							No donations yet — be the first on the wall.
						</div>
					) : (
						<ul className="space-y-3">
							{events.map((e) => (
								<li
									key={e.id}
									className="rounded-2xl border border-border/70 bg-gradient-to-br from-muted/40 to-muted/10 px-4 py-3"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="font-mono text-[11px] text-muted-foreground">
												{shortPrincipal(e.donor)}
											</p>
											<p className="mt-1 inline-flex items-center gap-1.5 font-mono-amount text-base font-bold">
												<TokenIcon token="ICP" size="sm" plain />
												{formatNum(e.amountIcp, 0)} ICP
											</p>
										</div>
										<span className="shrink-0 text-[10px] text-muted-foreground">
											{formatNsDate(e.donatedAtNs)}
										</span>
									</div>
									{e.message ? (
										<p className="mt-2 text-sm leading-snug text-foreground/90">
											“{e.message}”
										</p>
									) : (
										<p className="mt-2 text-xs italic text-muted-foreground">
											Message pending
										</p>
									)}
								</li>
							))}
						</ul>
					)}

					{total > WALL_PAGE ? (
						<Pagination
							offset={wallOffset}
							pageSize={WALL_PAGE}
							total={total}
							onOffsetChange={setWallOffset}
						/>
					) : null}
				</div>
			</div>

			<div className="flex flex-wrap gap-2">
				<Link to={ROUTES.agents} className="flex-1 sm:flex-none">
					<Button variant="secondary" fullWidth>
						Agent layer
					</Button>
				</Link>
				<Link to={ROUTES.analytics} className="flex-1 sm:flex-none">
					<Button fullWidth>Protocol metrics</Button>
				</Link>
			</div>
		</div>
	);
}
